import { API_BASE } from "../util/constants";
import { showError, showSuccess } from "./notification";
import { redirectToLogin, waitForSilentRefresh } from "../oauth";
import {
  AUTHENTICATION_REQUIRED_MESSAGE,
  AUTHENTICATION_REQUIRED_MESSAGE_KEY,
} from "../util/messageKeys";

function showNotLoggedIn($store) {
  const alreadyShown = $store.state.messages.some(({ key }) => key === AUTHENTICATION_REQUIRED_MESSAGE_KEY);
  if (!alreadyShown) {
    showError($store, AUTHENTICATION_REQUIRED_MESSAGE, { key: AUTHENTICATION_REQUIRED_MESSAGE_KEY });
  }
}

async function redirectForAuthenticationRequired($store) {
  showNotLoggedIn($store);
  const shouldRedirect = await $store.dispatch("beginLoginRedirect");
  await $store.dispatch("clearUserToken");
  if (shouldRedirect) {
    redirectToLogin({
      silent: false,
      originalRoute: window.location.pathname + window.location.search + window.location.hash,
    });
  }
  throw new Error('not logged in');
}

export async function withLoader($store, promiseFactory, enabled = true) {
  if (!enabled) {
    return await promiseFactory();
  }

  const loadID = {};
  await $store.dispatch("beginLoad", loadID);
  try {
    return await promiseFactory();
  } finally {
    $store.dispatch("endLoad", loadID).catch(() => null);
  }
}

export function callAPI($store, url, { query, showLoader, showErrorNotification = true, headers = {}, ...options } = {}) {
  return withLoader($store, async () => {
    await $store.dispatch("syncStoredAuth");

    let fullUrl = `${API_BASE}/${url}`;
    if (query) {
      fullUrl += '?' + new URLSearchParams(query).toString();
    }
    async function requestWithCurrentToken(allowRefreshedTokenRetry) {
      const token = $store.state.auth.token;
      if (!token) {
        return redirectForAuthenticationRequired($store);
      }

      const response = await fetch(fullUrl, {
        ...options,
        headers: {
          ...headers,
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 401 && allowRefreshedTokenRetry) {
        await $store.dispatch("syncStoredAuth");
        if (!$store.state.auth.token || $store.state.auth.token === token) {
          await waitForSilentRefresh(token);
          await $store.dispatch("syncStoredAuth");
        }
        if ($store.state.auth.token && $store.state.auth.token !== token) {
          return requestWithCurrentToken(false);
        }
      }

      return response;
    }

    const response = await requestWithCurrentToken(true);

    if (response.status === 401) { // Unauthorized
      return redirectForAuthenticationRequired($store);
    }

    if (response.status === 403) { // Forbidden
      // token is valid but we are not allowed to perform this operation
      const data= await response.json();
      if (showErrorNotification) {
        showError($store, data.message);
      }
      let error = new Error(data.message);
      error.code = data.code;
      throw error;
    }

    if (response.status >= 500) {
      console.error(`API call to ${url} returned ${response.status}`, await response.text());
      if (showErrorNotification) {
        showError($store, "Internal error.");
      }
      throw new Error('internal error');
    }

    if (response.status >= 400) {
      const data = await response.json();
      const message = data.message || data.detail;
      console.error(`API call to ${url} returned ${response.status}`, message);
      if (data.code === 'DUPLICATE_ITEM') {
        showSuccess($store, message);
      } else {
        if (showErrorNotification) {
          showError($store, message);
        }
      }
      let error = new Error(message);
      error.code = data.code;
      error.status = response.status;
      throw error;
    }

    if (response.status === 204) { // No Content
      return null;
    }

    const responseText = await response.text();
    if (!responseText.trim()) {
      return null;
    }

    return JSON.parse(responseText);

  }, showLoader);
}

export const apiMixins = {
  methods: {
    // usage: this.withLoader(async () => {...});
    withLoader(promiseFactory, enabled) {
      return withLoader(this.$store, promiseFactory, enabled);
    },
    callAPI(url, options) {
      return callAPI(this.$store, url, options);
    },
    showSuccess(message) { // Expose showSuccess
      showSuccess(this.$store, message);
    },
    showError(message) { // Expose showError
      showError(this.$store, message);
    },
  },
};
